// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { useAuthProviders, useLogin, useMe } from '../../api/auth.js';
import { BrandSymbol, BrandWordmark } from '../../components/BrandMark.js';

export function LoginRoute() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const { data: providers } = useAuthProviders();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/dashboard';

  if (me) {
    return <Navigate to={redirectTo} replace />;
  }

  // Full page navigation, not a fetch: the SSO leg is a browser redirect to
  // Astrum and back, so it cannot go through apiFetch. `redirectTo` is echoed
  // by the callback, which restricts it to relative paths on WEB_ORIGIN.
  const ssoHref = `/api/auth/astrum/start?redirectTo=${encodeURIComponent(redirectTo)}`;

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    login.mutate(
      { email, password },
      {
        onSuccess: () => {
          navigate(redirectTo, { replace: true });
        },
      },
    );
  };

  return (
    <main className="login-main" id="main-content">
      <div className="login-card">
        <div className="login-brand">
          <BrandSymbol className="brand-symbol" aria-hidden="true" focusable="false" />
          <BrandWordmark className="brand-wordmark" />
        </div>
        <h1 className="login-title">Sign in</h1>
        <form onSubmit={onSubmit} aria-describedby={login.error ? 'login-error' : undefined}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label htmlFor="login-email">
              <span>Email</span>
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                aria-required="true"
                aria-invalid={login.error ? true : undefined}
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                style={{ width: '100%' }}
              />
            </label>
            <label htmlFor="login-password">
              <span>Password</span>
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                aria-required="true"
                aria-invalid={login.error ? true : undefined}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                style={{ width: '100%' }}
              />
            </label>
            {login.error ? (
              <div id="login-error" className="error-banner" role="alert" aria-live="assertive">
                {login.error.message || 'Invalid email or password.'}
              </div>
            ) : null}
            <button
              type="submit"
              data-variant="primary"
              disabled={login.isPending}
              style={{ width: '100%' }}
            >
              {login.isPending ? 'Signing in…' : 'Sign in'}
            </button>
            <Link to="/forgot-password" className="text-sm" style={{ textAlign: 'center' }}>
              Forgot your password?
            </Link>
          </div>
        </form>
        {providers?.astrumSso === true ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
            <div className="login-divider" role="separator">
              <span>or</span>
            </div>
            <a data-ui-button="" data-variant="secondary" href={ssoHref} style={{ width: '100%' }}>
              Sign in with Astrum
            </a>
          </div>
        ) : null}
      </div>
    </main>
  );
}
